import { useContext } from "react";
import { Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import type { AccountsController } from "../../../../api/src/accounts/accounts.controller";
import type { UnpackResponse } from "../../../../api/src/lib";
import Loading from "../../Loading";
import AssetAllocationBarChart, {
  AssetAllocationSeries,
} from "../../components/AssetAllocationBarChart";
import {
  deserializeAllocationDetailsPercents,
  useAuthenticatedPercentFetch,
} from "../percents";
import { calculateExcludedSecuritiesPercentage } from "../../lib/excludedSecurities";
import { AccountContext } from "./AccountInfo";

const AccountAssetAllocationChart = () => {
  const { accountId } = useParams();
  const { account } = useContext(AccountContext);

  const {
    isPending: isPendingAllocationDetails,
    isError: isErrorAllocationDetails,
    data: currentAllocationDetails,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<AccountsController["getAccountAllocationDetails"]>
  >(
    `/v1/accounts/${accountId}/allocation-details`,
    deserializeAllocationDetailsPercents,
  );

  // Calculate percentage of excluded securities in holdings
  const excludedSecuritiesPercentage = account
    ? calculateExcludedSecuritiesPercentage(account)
    : 0;

  const data: AssetAllocationSeries[] = [
    {
      label: "Current",
      data: currentAllocationDetails?.data.current ?? [],
    },
    {
      label: "Account Target",
      data: currentAllocationDetails?.data.target ?? [],
    },
  ];

  return isPendingAllocationDetails ? (
    <Loading message="Asset Allocation" />
  ) : isErrorAllocationDetails ? (
    <Alert variant="danger">Failed to load Allocation Details</Alert>
  ) : (currentAllocationDetails?.data.current.length ?? 0) === 0 &&
    (currentAllocationDetails?.data.target.length ?? 0) === 0 ? (
    <Alert variant="info">No Allocation Details</Alert>
  ) : (
    <AssetAllocationBarChart
      data={data}
      excludedSecuritiesPercentage={excludedSecuritiesPercentage}
    />
  );
};

export default AccountAssetAllocationChart;
